import { getSupabase } from "../auth/supabaseClient";
import type { RunState, SoloMode } from "../game/types";

function normalizeLevelAdvanceBaseScore(levelScore: number, value: number | null | undefined): number {
  const normalizedLevelScore = typeof levelScore === "number" && Number.isFinite(levelScore)
    ? Math.max(0, Math.floor(levelScore))
    : 0;
  const normalizedValue = typeof value === "number" && Number.isFinite(value)
    ? Math.floor(value)
    : 0;
  return Math.max(0, Math.min(normalizedLevelScore, normalizedValue));
}

/** In-memory version tracker per (userId, mode) pair for optimistic locking. */
const knownVersions = new Map<string, number>();

function versionKey(userId: string, mode: SoloMode): string {
  return `${userId}::${mode}`;
}

export async function saveRunStateCloud(
  userId: string,
  run: RunState,
  mode: SoloMode = run.soloMode ?? "classic"
): Promise<boolean> {
  try {
    const supabase = getSupabase();
    const key = versionKey(userId, mode);
    const currentVersion = knownVersions.get(key) ?? 0;
    const nextVersion = currentVersion + 1;

    // Optimistic lock: only update if the row's version matches what we last read.
    // If no row exists yet (currentVersion === 0), use upsert to create it.
    if (currentVersion === 0) {
      const { error } = await supabase
        .from("game_saves")
        .upsert(
          {
            user_id: userId,
            solo_mode: mode,
            run_state: {
              ...run,
              soloMode: run.soloMode ?? mode,
              levelAdvanceBaseScore: normalizeLevelAdvanceBaseScore(run.levelScore, run.levelAdvanceBaseScore)
            },
            schema_version: 1,
            save_version: nextVersion,
            updated_at: new Date().toISOString()
          },
          { onConflict: "user_id,solo_mode" }
        );
      if (error != null) return false;
      knownVersions.set(key, nextVersion);
      return true;
    }

    // Existing row: conditional update with version check
    const { data, error } = await supabase
      .from("game_saves")
      .update({
        run_state: {
          ...run,
          soloMode: run.soloMode ?? mode,
          levelAdvanceBaseScore: normalizeLevelAdvanceBaseScore(run.levelScore, run.levelAdvanceBaseScore)
        },
        schema_version: 1,
        save_version: nextVersion,
        updated_at: new Date().toISOString()
      })
      .eq("user_id", userId)
      .eq("solo_mode", mode)
      .eq("save_version", currentVersion)
      .select("save_version")
      .single();

    if (error != null || data == null) {
      // Version conflict — another tab wrote first. Reload to get fresh version.
      console.warn("[cloudSave] version conflict, reloading latest version");
      await loadRunStateCloud(userId, mode);
      return false;
    }

    knownVersions.set(key, nextVersion);
    return true;
  } catch {
    return false;
  }
}

export async function loadRunStateCloud(userId: string, mode: SoloMode = "classic"): Promise<RunState | null> {
  try {
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from("game_saves")
      .select("run_state, save_version")
      .eq("user_id", userId)
      .eq("solo_mode", mode)
      .single();
    if (error || data == null) return null;

    // Track the loaded version for optimistic locking
    const version = typeof data.save_version === "number" ? data.save_version : 0;
    knownVersions.set(versionKey(userId, mode), version);

    const run = data.run_state as RunState;
    return {
      ...run,
      soloMode: run.soloMode ?? mode,
      levelAdvanceBaseScore: normalizeLevelAdvanceBaseScore(run.levelScore, run.levelAdvanceBaseScore)
    };
  } catch {
    return null;
  }
}

export async function clearRunStateCloud(userId: string, mode: SoloMode = "classic"): Promise<void> {
  try {
    const supabase = getSupabase();
    await supabase.from("game_saves").delete().eq("user_id", userId).eq("solo_mode", mode);
    knownVersions.delete(versionKey(userId, mode));
  } catch {
    // Silently fail - local save is the primary
  }
}
